import { formatInTimeZone } from "date-fns-tz";
import { useMemo } from "react";
import { useAppSelector } from "../../../lib/redux/hooks";
import Pills from "../../../ui/molecoles/pills";
import { HeadingComponent } from "../../../ui/organisms/heading";
import { HeadingSize } from "../../../ui/organisms/heading/dto";
import { Notam } from "../dto";

export const CRITICAL_NOTAM_RULES = [
    {
        id: 'ad_closed',
        category: 'Infrastructure', 
        regex: /\b(AD|AERODROME|AIRPORT|AIRFIELD)\s+(CLSD|CLOSED|CLOSURE)\b/i
    },
    {
        id: 'rwy_closed',
        category: 'Infrastructure', 
        regex: /\b(RWY|RUNWAY)\s+(\d{2}[LCR]?|\d{2})\s+(CLSD|CLOSED|CLOSURE)\b/i
    },
    {
        id: 'fire_cat',
        category: 'Safety', 
        regex: /\b(ARFF|FIRE\s+CAT|FIRE\s+CATEGORY|RFFS)\b.*\b(NOT\s+AVBL|NOT\s+AVAILABLE|DOWNGRADED|NOT\s+PROVIDED)\b/i
    },
    {
        id: 'atc_down',
        category: 'Air Traffic Control', 
        regex: /\b(TWR|TOWER|APP|APPROACH|ATC|RADAR)\s+(NOT\s+AVBL|NOT\s+AVAILABLE|CLSD|CLOSED|SUSPENDED)\b/i
    },
    {
        id: 'braking_poor',
        category: 'Runway Condition', 
        regex: /\b(BRAKING\s+ACTION|FRICTION)\b.*\b(POOR|UNRELIABLE|NIL|MEDIUM\s+TO\s+POOR)\b/i
    },
    {
        id: 'prohibited_area',
        category: 'Airspace',
        regex: /\b(TEMPORARY\s+PROHIBITED\s+AREA|PROHIBITED\s+AREA|RESTRICTED\s+AREA|DANGER\s+AREA)\b/i
    },
    {
        id: 'navaid_out',
        category: 'Navigation', 
        regex: /\b(ILS|VOR|GLIDESLOPE|GP|LOC|LOCALIZER)\b.*\b(U\/S|UNSERVICEABLE|OUT\s+OF\s+SERVICE|NOT\s+AVBL)\b/i
    },
    {
        id: 'security',
        category: 'Security', 
        regex: /\b(SECURITY\s+THREAT|EVACUATION|BOMB|TERROR|ATTACK)\b/i
    }
];

export function ArrivalNotam() {
    const reportState = useAppSelector(state => state.report);

    let rawNotamList: Notam[] = [];
    if (reportState.findByIdStatus === "successfully" && reportState.findByIdResponse?.flight_info.arrival_airport_notams) {
        rawNotamList = reportState.findByIdResponse.flight_info.arrival_airport_notams;
    }

    const timezone = reportState.findByIdResponse?.flight_info.flight.arrival.airport.timezone || 'UTC';

    const filteredNotams = useMemo(() => {
        return rawNotamList.reduce((acc, notam) => {
            const textToAnalyze = (notam.message + " " + notam.description).toUpperCase();
            const matchedRule = CRITICAL_NOTAM_RULES.find(rule => rule.regex.test(textToAnalyze));

            if (matchedRule) {
                acc.push({
                    ...notam,
                    _filterCategory: matchedRule.category
                });
            }
            return acc;
        }, [] as (Notam & { _filterCategory: string })[]);
    }, [rawNotamList]);

    return (
        <>
            <HeadingComponent
                label={
                    (reportState.findByIdResponse?.flight_info.flight.arrival.airport.city || "") + " Airport" +
                    " (" + (reportState.findByIdResponse?.flight_info.flight.arrival.airport.code_iata || "") + ")"
                }
                buttons={[]}
                size={HeadingSize.SM}
            />
            {
                filteredNotams.length === 0 ?
                    <div className="text-text-md font-semibold text-pas-800">
                        No critical NOTAMs available for this airport.
                    </div>
                    :
                    <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem' }}>
                        {filteredNotams.map((notam, idx) => (
                            <li key={idx}>
                                <div className="flex flex-col gap-2 mt-5">
                                    <div className="flex flex-row gap-2 items-center">
                                        <span className="text-text-md font-semibold text-pas-800">
                                            {notam.startdate.toString() === "2222-02-02T02:02:00Z" ? "N/A " : formatInTimeZone(notam.startdate, timezone, 'dd/MM/yyyy HH:mm') + " "}
                                            -
                                            {notam.enddate.toString() === "2222-02-02T02:02:00Z" ? " N/A" : " " + formatInTimeZone(notam.enddate, timezone, 'dd/MM/yyyy HH:mm')}
                                        </span>
                                        <Pills
                                            size={"sm"}
                                            label={notam._filterCategory}
                                            outline={true}
                                            emphasis={true}
                                            color={"blue"}
                                        />
                                    </div>
                                    <span className="pl-4 text-text-md font-normal text-neutral-900">
                                        {notam.ai_interpretation || notam.description || "N/A"}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
            }
        </>
    )
}